import type { ReactNode } from 'react'
import { Badge, Button, Paper, Select } from '@mantine/core'
import type {
  PopulationAgeGroup,
  PopulationKind,
  PopulationRiskLevel,
} from '@behuman/core-contract'
import { useProviderOptions, useStateOptions, type PopulationQuery } from '../hooks'
import { AGE_GROUP_LABELS, KIND_LABELS, RISK_LEVEL_LABELS, options, stateLabel } from '../labels'
import type { ParamKey } from '../usePopulationParams'
import { withSelected } from '../utils'

/**
 * The "Viewing" row: Provider, State, Age group, Risk level and Type filters, with the active
 * count and Clear, and `trailing` (the data's freshness) on the right.
 */
export function FilterBar({
  query,
  setParam,
  clearFilters,
  activeFilterCount,
  trailing,
}: {
  query: PopulationQuery
  setParam: (key: ParamKey, value: string | null) => void
  clearFilters: () => void
  activeFilterCount: number
  trailing?: ReactNode
}) {
  const providers = useProviderOptions(query)
  const states = useStateOptions()

  // A filter from a shared URL stays visible even when no option matches it, so an active
  // filter is never hidden behind an empty select.
  const providerData = withSelected(
    (providers.data?.providers ?? []).map((p) => ({
      value: p.providerId,
      label: p.providerName ?? p.providerId,
    })),
    query.providerId,
  )
  const stateData = withSelected(
    (states.data?.states ?? []).map((state) => ({ value: state, label: stateLabel(state) })),
    query.state,
  )

  const common = { clearable: true, size: 'sm', w: 160 } as const

  // A failed options request says so, with a retry, instead of leaving an empty select.
  const optionsError = (what: string, retry: () => unknown) => (
    <span className="inline-flex items-center gap-1">
      Couldn’t load {what}
      <Button variant="subtle" color="red" size="compact-xs" onClick={() => void retry()}>
        Retry
      </Button>
    </span>
  )

  return (
    <Paper withBorder radius="lg" px="md" py="sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-[11px] font-semibold tracking-wider text-slate-500 uppercase">
          Viewing
        </span>
        <Select
          {...common}
          aria-label="Provider"
          placeholder="All providers"
          searchable
          data={providerData}
          error={providers.isError && optionsError('providers', providers.refetch)}
          value={query.providerId ?? null}
          onChange={(value) => setParam('providerId', value)}
          w={220}
        />
        <Select
          {...common}
          aria-label="State"
          placeholder="All states"
          searchable
          data={stateData}
          error={states.isError && optionsError('states', states.refetch)}
          value={query.state ?? null}
          onChange={(value) => setParam('state', value)}
        />
        <Select
          {...common}
          aria-label="Age group"
          placeholder="All ages"
          data={options<PopulationAgeGroup>(AGE_GROUP_LABELS)}
          value={query.ageGroup ?? null}
          onChange={(value) => setParam('ageGroup', value)}
        />
        <Select
          {...common}
          aria-label="Risk level"
          placeholder="All levels"
          data={options<PopulationRiskLevel>(RISK_LEVEL_LABELS)}
          value={query.riskLevel ?? null}
          onChange={(value) => setParam('riskLevel', value)}
        />
        <Select
          {...common}
          aria-label="Type"
          placeholder="Care gaps and risks"
          data={options<PopulationKind>(KIND_LABELS)}
          value={query.kind ?? null}
          onChange={(value) => setParam('kind', value)}
        />
        <div className="flex items-center gap-2">
          {activeFilterCount > 0 && (
            <Badge variant="light" aria-label={`${activeFilterCount} active filters`}>
              {activeFilterCount} active
            </Badge>
          )}
          <Button
            variant="subtle"
            size="compact-sm"
            onClick={clearFilters}
            disabled={activeFilterCount === 0}
          >
            Clear filters
          </Button>
        </div>
        {trailing && <div className="ml-auto">{trailing}</div>}
      </div>
    </Paper>
  )
}
